import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { Timeout } from '../../../types'
import { API_MODELS_YOU_MAKE_LIKE_MODELS } from '../../constants/endpoints'
import { ListType } from '../../constants/enums'
import {
    MYML_LOCATIONS,
    MultipleModelRecommendationsResponse,
    Recommendation,
} from '../../mfcLogic/api/modelsYouMayLike'
import { pubSubMyml } from '../../state/pubSub'
import { MYMLSTATE, MymlEvent, setMymlResults } from '../../state/state.myml'
import useFetch from './useFetch'
import { useList } from './useList'
import { usePassive } from './usePassive'

const getMymlState = (location: MYML_LOCATIONS) => () => MYMLSTATE[location]

const subMyml = (callback: () => void) =>
    pubSubMyml.on(MymlEvent.UPDATE_RESULTS, callback)

function sanitizeRecommendations(
    broadcastingModels: Set<number>,
    apiResults: Array<Recommendation>,
): Array<Recommendation> {
    const recommendedModels: Array<Recommendation> = []
    apiResults.forEach((entry) => {
        // Ensure recommended model is broadcasting in public (free) chat
        if (
            broadcastingModels.size > 0 &&
            !broadcastingModels.has(entry.user_id)
        ) {
            return
        }
        recommendedModels.push(entry)
    })
    return recommendedModels
}

const useModelsYouMayLike = (
    id: number,
    location: MYML_LOCATIONS,
    inRoomOrProfile?: boolean,
) => {
    // const { id } = props
    const passiveMode = usePassive()
    const timer = useRef<Timeout>(undefined)
    const {
        results: mymlStateResults,
        id: lastId,
        timestamp,
    } = useSyncExternalStore(
        subMyml,
        getMymlState(location),
        getMymlState(location),
    )
    const models = useList(ListType.DEFAULT_PUBLIC, { returnIDs: true })
    const publicModels = useMemo(() => new Set(models), [models])
    const [loading, setLoading] = useState(true)
    const [fetchFailed, setFetchFailed] = useState(false)
    const fetchOptions = {
        url: `${API_MODELS_YOU_MAKE_LIKE_MODELS}?model_ids=${id}&user_details=1&no_cache=${Date.now()}`,
    }
    const [rawModelsYouMayLike, fetchMYML] =
        useFetch<MultipleModelRecommendationsResponse>()
    const getLatestRecommendations = useCallback(async () => {
        try {
            setLoading(true)
            // console.log('[getLatestRecommendations] fetching')
            fetchMYML(fetchOptions)
        } catch (_error) {
            setFetchFailed(true)
        }
    }, [id, fetchOptions])

    useEffect(() => {
        if (!id) return

        clearInterval(timer.current)
        if (passiveMode && !inRoomOrProfile) return
        if (!fetchFailed) {
            // only run this if we:
            // 1) haven't run it for this id in the previous 45s and we have results already
            const newTimestamp = +new Date()
            const lastRunCheck = newTimestamp > timestamp + 45000
            // 2) had the id change for the results
            const lastIdCheck = lastId !== id

            // console.log(
            //     '[useModelsYouMayLike.useEffect]',
            //     {
            //         lastRunCheck,
            //         lastIdCheck,
            //         emptyResults,
            //         rawModelsYouMayLike,
            //     },
            //     { newTimestamp, timestamp, length: mymlStateResults.length },
            // )
            if (lastIdCheck || lastRunCheck) {
                getLatestRecommendations()
            } else setLoading(false)

            timer.current = setInterval(() => {
                getLatestRecommendations()
            }, 45000)
        } else if (fetchFailed) {
            timer.current = setTimeout(() => {
                getLatestRecommendations()
            }, 1000)
        }
        return () => {
            clearInterval(timer.current)
        }
    }, [id, lastId, fetchFailed, passiveMode, timestamp])

    useEffect(() => {
        if (!rawModelsYouMayLike) return
        const { err, result: unprocessedResults } = rawModelsYouMayLike
        if (!err && unprocessedResults) {
            // actively filter as public model status changes
            const results = sanitizeRecommendations(
                publicModels,
                unprocessedResults.recommendations || [],
            )
            setMymlResults(id, results, location)
            setLoading(false)
        }
    }, [id, publicModels, rawModelsYouMayLike])

    return { loading, modelResults: mymlStateResults }
}

export default useModelsYouMayLike
